/**
 * Have-Done Web App
 *
 * Main entry point for the web application.
 * Celebrates accomplishments, not obligations.
 */

import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { useConvexAuth } from 'convex/react';
import {
  TodayScreen,
  TimelineScreen,
  SummaryScreen,
  SignInScreen,
  OfflineIndicator,
  OnboardingTour,
  useOnboardingStatus,
  QuickAddButton,
  LandingPage,
  AddEntryModal,
  UserMenu,
  ConsentBanner,
  AnalyticsConsent,
  PrivacyPage,
  TermsPage,
  PrivacySettings,
  ContactPage,
  Footer,
} from './components';
import { useKeyboardShortcuts } from './hooks';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

function Layout({ children }: { children: React.ReactNode }) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Enable global keyboard shortcuts
  useKeyboardShortcuts({
    onNewEntry: () => setIsAddModalOpen(true),
  });

  return (
    <div className="min-h-screen bg-bg-primary flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border-light bg-bg-primary/95 backdrop-blur">
        <div className="mx-auto max-w-3xl px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <NavLink to="/" className="flex items-center gap-2 shrink-0 hover:opacity-90 transition-opacity">
              {/* Logo icon - hidden on mobile */}
              <div className="hidden sm:flex w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-primary to-primary-dark items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-display text-xl sm:text-2xl text-primary whitespace-nowrap">Have-Done</span>
            </NavLink>
            <nav className="flex items-center gap-2 sm:gap-4">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `text-xs sm:text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-text-primary'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`
                }
              >
                Today
              </NavLink>
              <NavLink
                to="/timeline"
                className={({ isActive }) =>
                  `text-xs sm:text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-text-primary'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`
                }
              >
                Timeline
              </NavLink>
              <NavLink
                to="/summary"
                className={({ isActive }) =>
                  `text-xs sm:text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-text-primary'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`
                }
              >
                Summary
              </NavLink>
              <div className="ml-1 sm:ml-2">
                <UserMenu />
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-3xl w-full px-3 sm:px-4 py-8 flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer showKeyboardShortcuts />

      {/* Quick add button (FAB) */}
      <QuickAddButton onClick={() => setIsAddModalOpen(true)} />

      {/* Add Entry Modal */}
      <AddEntryModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      {/* Offline indicator */}
      <OfflineIndicator />
    </div>
  );
}

function AuthenticatedApp() {
  const { hasCompletedOnboarding, completeOnboarding, isLoading } = useOnboardingStatus();

  // Wait for onboarding status to load
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <BrowserRouter>
      {/* Analytics consent manager - syncs consent state with PostHog */}
      <AnalyticsConsent />

      <Routes>
        {/* Public pages - accessible without layout */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Protected pages - with layout */}
        <Route
          path="/*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<TodayScreen />} />
                <Route path="/timeline" element={<TimelineScreen />} />
                <Route path="/summary" element={<SummaryScreen />} />
                <Route path="/settings" element={<PrivacySettings />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>

      {/* Consent banner for analytics */}
      <ConsentBanner />

      {/* Onboarding tour for first-time users */}
      {!hasCompletedOnboarding && (
        <OnboardingTour onComplete={completeOnboarding} />
      )}
    </BrowserRouter>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center">
      <div className="text-center space-y-4">
        <h1 className="font-display text-3xl text-primary">Have-Done</h1>
        <div className="animate-pulse text-text-secondary">Loading...</div>
      </div>
    </div>
  );
}

function UnauthenticatedApp() {
  const [showSignIn, setShowSignIn] = useState(false);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public pages - always accessible */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Landing/Sign in */}
        <Route
          path="/*"
          element={
            showSignIn ? (
              <SignInScreen onBack={() => setShowSignIn(false)} />
            ) : (
              <LandingPage
                onGetStarted={() => setShowSignIn(true)}
                onLogin={() => setShowSignIn(true)}
              />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useConvexAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <UnauthenticatedApp />;
  }

  return <AuthenticatedApp />;
}
